/**
 * ddqOrder.ts — shared DDQ section/question ordering helper.
 *
 * Extracted from AccordionRenderer.tsx (~:97-137) so both the accordion and
 * the Workroom ledger share a SINGLE ordering source. The accordion's inline
 * copy is replaced by a call to `buildDdqSections`.
 *
 * No React dependency — pure data transform.
 */

import type { DDQAnsweredQuestion, DDQParsedQuestions } from "@/lib/api";

/** Pending placeholder used while streaming. */
export type PendingQuestion = {
  pending: true;
  question_id: string;
  section: string;
  question: string;
  sub_questions?: string[];
};

export type SectionItem = DDQAnsweredQuestion | PendingQuestion;

export function isPending(item: SectionItem): item is PendingQuestion {
  return (item as PendingQuestion).pending === true;
}

export type DdqSection = {
  name: string;
  items: SectionItem[];
};

/**
 * Build an ordered list of sections with their items, following the LP's
 * original DDQ order when `parsedStructure` is provided. Falls back to
 * arrival order otherwise.
 *
 * When `showTransient` is true, placeholders are emitted for every question
 * in the parsed structure that has not yet arrived (streaming).
 *
 * Behaviour is BYTE-IDENTICAL to the previous inline logic in AccordionRenderer.
 */
export function buildDdqSections(
  questions: DDQAnsweredQuestion[],
  parsedStructure?: DDQParsedQuestions | null,
  showTransient = false,
): DdqSection[] {
  if (parsedStructure?.sections?.length) {
    const byId = new Map<string, DDQAnsweredQuestion>();
    for (const q of questions) byId.set(q.question_id, q);

    const out: DdqSection[] = [];
    for (const sec of parsedStructure.sections) {
      const items: SectionItem[] = [];
      for (const q of sec.questions) {
        const r = byId.get(q.id);
        if (r) {
          items.push(r);
        } else if (showTransient) {
          items.push({
            pending: true,
            question_id: q.id,
            section: sec.name,
            question: q.text,
            sub_questions: q.sub_questions,
          });
        }
      }
      if (items.length) out.push({ name: sec.name, items });
    }

    // Append any results whose section isn't in the structure (defensive).
    const placed = new Set(out.flatMap((s) => s.items.map((i) => i.question_id)));
    const leftovers: SectionItem[] = [];
    for (const q of questions) {
      if (!placed.has(q.question_id)) leftovers.push(q);
    }
    if (leftovers.length) out.push({ name: "Other", items: leftovers });
    return out;
  }

  // Arrival order fallback.
  const order: string[] = [];
  const map = new Map<string, SectionItem[]>();
  for (const q of questions) {
    if (!map.has(q.section)) {
      map.set(q.section, []);
      order.push(q.section);
    }
    map.get(q.section)!.push(q);
  }
  return order.map((name) => ({ name, items: map.get(name)! }));
}

/**
 * Flatten sections into a flat list interleaving section-header rows and
 * question rows. Used by the virtualizer to compute a single flat index
 * space.
 */
export type FlatRow =
  | { kind: "section"; name: string; totalQuestions: number; sectionIndex: number }
  | { kind: "question"; question: DDQAnsweredQuestion; sectionName: string; sectionIndex: number };

export function flattenDdqRows(sections: DdqSection[]): FlatRow[] {
  const rows: FlatRow[] = [];
  sections.forEach((sec, si) => {
    const answered = sec.items.filter((i) => !isPending(i)) as DDQAnsweredQuestion[];
    rows.push({
      kind: "section",
      name: sec.name,
      totalQuestions: sec.items.length,
      sectionIndex: si,
    });
    for (const item of sec.items) {
      if (!isPending(item)) {
        rows.push({
          kind: "question",
          question: item,
          sectionName: sec.name,
          sectionIndex: si,
        });
      }
    }
    void answered; // keep compiler happy
  });
  return rows;
}
